/**
 * Image upload utility for Supabase Storage
 * This file provides a reusable function to upload images to Supabase storage
 */

import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

// Initialize Supabase client for image storage
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Uploads an image file to Supabase Storage and returns the public URL
 * 
 * @param {File} file - The image file to upload
 * @param {string} bucket - The storage bucket name (defaults to 'product-images')
 * @returns {Promise<string>} - A promise that resolves to the public URL of the uploaded image
 */
export const uploadImage = async (file, bucket = 'product-images') => {
  try {
    if (!file) throw new Error('No file provided for upload');
    
    // Extract file extension and create a unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`;

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;
    
    // Get public URL for the uploaded image
    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    if (!urlData?.publicUrl) {
      throw new Error('Could not get public URL for uploaded image');
    }

    return urlData.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}; 